const DEVICE_FINGERPRINT = "DEVICE_FINGERPRINT";
const CACHE_COOKIE = "onedevice_cached";
const SCRIPT_ID = "onedevice-collect";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const ONE_DEVICE_POLL_MS = 50;
const ONE_DEVICE_TIMEOUT_MS = 5000;

function deviceOrigin(): string {
  let hostname = "";
  try {
    hostname = location.hostname || "";
  } catch {}
  while (hostname.startsWith("play.") || hostname.startsWith("s.")) {
    hostname = hostname.replace(/^(?:play\.|s\.)/, "");
  }
  if (!hostname || hostname === "localhost") {
    hostname = "winaz.com";
  }
  return `https://device.${hostname}`;
}

type OneDeviceApi = {
  identify: (options: {
    endpoint: string;
  }) => Promise<{ visitor_id?: string; id_source?: string }>;
};

type WindowWithOneDevice = Window & {
  OneDevice?: OneDeviceApi;
  __oneDeviceFailed?: boolean;
  __oneDeviceId?: Promise<string>;
};

/** Inject collect.js sớm. Skip nếu đã có DEVICE_FINGERPRINT hoặc script đã có. */
export const injectOneDeviceCollect = (): void => {
  if (typeof document === "undefined") return;
  if (readCache()) return;
  if ((window as WindowWithOneDevice).OneDevice) return;
  if (document.querySelector(`script[id="${SCRIPT_ID}"]`)) return;

  const origin = deviceOrigin();
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = `${origin}/v1/collect.js`;
  script.async = true;
  document.head.appendChild(script);
};

const resolveFingerprint = async (): Promise<string> => {
  const visitorId = await identify();
  if (visitorId) {
    writeCache(visitorId);
    return visitorId;
  }

  const fingerprint = await buildFingerprint();
  writeCache(fingerprint);
  return fingerprint;
};

let pending: Promise<string> | null = null;

/**
 * 1. cache localStorage
 * 2. OneDevice identify
 * 3. buildFingerprint
 */
export const getDeviceFingerprint = (): Promise<string> => {
  const cached = readCache();
  if (cached) return Promise.resolve(cached);

  // App warms this in parallel with getBootstrap and resolveExperimentTheme
  // asks for it again later; share one run so identify() is not hit twice.
  if (!pending) {
    pending = resolveFingerprint();
    pending.catch(() => {
      pending = null;
    });
  }
  return pending;
};

export const getDeviceIdLocalStorage = () => {
  return readCache();
};

// The edge cannot read localStorage, so this cookie is how it learns the
// device is already known and the collect.js tag can be left out. It holds a
// flag, never the id itself.
const setCacheCookie = (alive: boolean) => {
  try {
    const age = alive ? CACHE_TTL_MS / 1000 : 0;
    document.cookie = `${CACHE_COOKIE}=${alive ? "1" : ""}; path=/; max-age=${age}; samesite=lax`;
  } catch {}
};

// Dropping the cookie on a miss lets the pair heal itself: the next request
// gets the tag back rather than silently falling through to the slow path.
const missCache = (): string => {
  setCacheCookie(false);
  return "";
};

const readCache = (): string => {
  try {
    const raw = localStorage.getItem(DEVICE_FINGERPRINT);
    if (!raw) return missCache();
    const cached = JSON.parse(raw) as { value?: string; exp?: number };
    if (!cached?.value || !cached.exp || Date.now() > cached.exp) {
      return missCache();
    }
    return cached.value;
  } catch {
    return missCache();
  }
};

const writeCache = (id: string) => {
  if (!id) return;
  try {
    localStorage.setItem(
      DEVICE_FINGERPRINT,
      JSON.stringify({ value: id, exp: Date.now() + CACHE_TTL_MS }),
    );
    setCacheCookie(true);
  } catch {}
};

const identify = async (): Promise<string> => {
  if (typeof document === "undefined") return "";

  // build.js starts this from the HTML, so by now it is usually already
  // answered. Empty means it gave up, and the path below can still retry.
  const early = (window as WindowWithOneDevice).__oneDeviceId;
  if (early) {
    try {
      const id = await early;
      if (id) return id;
    } catch {}
  }

  const origin = deviceOrigin();

  try {
    const api = await loadOneDevice(origin);
    if (!api) return "";

    const r = await api.identify({ endpoint: `${origin}/api/identify` });
    return r?.visitor_id || "";
  } catch (err) {
    console.error(err);
    return "";
  }
};

const loadOneDevice = (origin: string): Promise<OneDeviceApi | null> => {
  const existing = (window as WindowWithOneDevice).OneDevice;
  if (existing) return Promise.resolve(existing);

  // Set by the tag build.js puts in the HTML, whose error may have fired long
  // before this call.
  if ((window as WindowWithOneDevice).__oneDeviceFailed) {
    return Promise.resolve(null);
  }

  // Đảm bảo script đã inject (no-op nếu inject sớm rồi)
  injectOneDeviceCollect();

  const collectSrc = `${origin}/v1/collect.js`;
  const script = document.querySelector(
    `script[id="${SCRIPT_ID}"]`,
  ) as HTMLScriptElement | null;
  if (!script) return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (api: OneDeviceApi | null) => {
      if (settled) return;
      settled = true;
      window.clearInterval(poll);
      window.clearTimeout(timer);
      resolve(api);
    };

    // The tag is served in the HTML, so the script can finish before this
    // listener is attached — its load event would then never reach us. Poll
    // for the global as well, and give up rather than hang if it never lands.
    const poll = window.setInterval(() => {
      const api = (window as WindowWithOneDevice).OneDevice;
      if (api) finish(api);
    }, ONE_DEVICE_POLL_MS);
    const timer = window.setTimeout(() => finish(null), ONE_DEVICE_TIMEOUT_MS);

    script.addEventListener(
      "load",
      () => {
        const api = (window as WindowWithOneDevice).OneDevice;
        if (api) finish(api);
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => {
        console.error(`Failed to load: ${collectSrc}`);
        finish(null);
      },
      { once: true },
    );

    const ready = (window as WindowWithOneDevice).OneDevice;
    if (ready) finish(ready);
  });
};

const buildFingerprint = async (): Promise<string> => {
  const fnv1a = (data: Uint8Array | Uint8ClampedArray): string => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < data.length; i++) {
      hash ^= data[i];
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(16).padStart(8, "0");
  };

  const getCanvasHash = (): string => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 60;
      canvas.height = 10;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return "";
      ctx.textBaseline = "top";
      ctx.font = "10px Arial";
      ctx.fillStyle = "#f60";
      ctx.fillRect(2, 2, 20, 5);
      ctx.fillStyle = "#069";
      ctx.fillText("FP", 1, 1);
      return fnv1a(ctx.getImageData(0, 0, 20, 5).data);
    } catch {
      return "";
    }
  };

  let timezone = "";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {}

  const screenW = Math.min(window.screen.width, window.screen.height);
  const screenH = Math.max(window.screen.width, window.screen.height);
  const raw = [
    navigator.platform || "",
    timezone,
    screenW,
    screenH,
    window.screen.colorDepth,
    window.devicePixelRatio || 1,
    navigator.hardwareConcurrency || "",
    navigator.maxTouchPoints || 0,
    getCanvasHash(),
    Date.now(),
    Math.random().toString(36).substring(2, 15),
  ].join("||");

  const byteData = new TextEncoder().encode(raw);
  let hash = "";
  if (window.crypto?.subtle) {
    try {
      const hashBuffer = await crypto.subtle.digest("SHA-256", byteData);
      hash = Array.from(new Uint8Array(hashBuffer))
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("");
    } catch {
      hash = fnv1a(byteData);
    }
  } else {
    hash = fnv1a(byteData);
  }
  return `m-${hash}`;
};
